import { useState } from 'react';
import { MessageCircle, Send, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { whatsappService } from '../services/whatsappService';

export function WhatsappSend() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [sendMode, setSendMode] = useState<'text' | 'template'>('text');
  const [templateName, setTemplateName] = useState('hello_world');
  const [templateLanguageCode, setTemplateLanguageCode] = useState('en_US');
  const [loading, setLoading] = useState(false);
  const [responsePreview, setResponsePreview] = useState<any>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!phoneNumber.trim() || (sendMode === 'text' && !message.trim()) || (sendMode === 'template' && !templateName.trim())) {
      toast.error('Telefon numarası ve seçili gönderim tipi için zorunlu alanlar eksik');
      return;
    }

    try {
      setLoading(true);
      const response: any = await whatsappService.send(sendMode === 'template'
        ? { phoneNumber, templateName, templateLanguageCode }
        : { phoneNumber, message });
      setResponsePreview(response?.data || response);
      toast.success(response?.message || 'WhatsApp mesajı gönderildi');
    } catch (error: any) {
      console.error('WhatsApp send admin page error:', error);
      setResponsePreview(error?.response?.data || null);
      toast.error(error?.message || 'WhatsApp mesajı gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full p-6 space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-green-100 text-green-700 flex items-center justify-center">
            <MessageCircle className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WhatsApp Send</h1>
            <p className="text-sm text-gray-500">Admin panelinden doğrudan WhatsApp mesaj gönderimini test edin.</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gönderim Tipi</label>
            <select value={sendMode} onChange={(e) => setSendMode(e.target.value as 'text' | 'template')} className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 mb-5">
              <option value="text">Serbest Metin</option>
              <option value="template">Template</option>
            </select>
            <label className="block text-sm font-medium text-gray-700 mb-2">Telefon Numarası</label>
            <div className="relative">
              <Smartphone className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="905XXXXXXXXX"
                className="w-full rounded-xl border border-gray-300 pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-600">
            <div className="font-semibold text-gray-800 mb-2">Örnek JSON</div>
            <pre className="whitespace-pre-wrap break-words text-xs">{sendMode === 'template' ? `{
  "phoneNumber": "905321234567",
  "templateName": "hello_world",
  "templateLanguageCode": "en_US"
}` : `{
  "phoneNumber": "905321234567",
  "message": "Merhaba"
}`}</pre>
          </div>
        </div>

        {sendMode === 'template' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Template Adı</label>
              <input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="hello_world" className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dil Kodu</label>
              <input value={templateLanguageCode} onChange={(e) => setTemplateLanguageCode(e.target.value)} placeholder="en_US" className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mesaj</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Gönderilecek WhatsApp mesajını yazın..."
              className="w-full min-h-[260px] rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => { setPhoneNumber(''); setMessage(''); setTemplateName('hello_world'); setTemplateLanguageCode('en_US'); setResponsePreview(null); }} className="px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50">Temizle</button>
          <button type="submit" disabled={loading} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-60">
            <Send className="w-4 h-4" />
            {loading ? 'Gönderiliyor...' : 'Mesaj Gönder'}
          </button>
        </div>
      </form>

      {responsePreview && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="text-sm font-semibold text-gray-800 mb-3">API Yanıtı</div>
          <pre className="text-xs bg-gray-50 border border-gray-200 rounded-xl p-4 overflow-auto whitespace-pre-wrap break-words">{JSON.stringify(responsePreview, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
